var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable no-nested-ternary */
import React, { useState } from 'react';
import SelectLib, { components, } from 'react-select';
import CreatableSelectLib from 'react-select/creatable';
import AsyncSelectLib from 'react-select/async';
import styled from 'styled-components';
import css from '@styled-system/css';
import spotnanaTheme from '../utils/themes';
import Box from '../Box';
import { themeSelector } from '../utils';
const { colors } = spotnanaTheme;
export const SelectWrapper = styled.div((props) => css({
    position: 'relative',
    width: 'full',
    '.select__input': {
        fontSize: '16px',
    },
    '.select__control': {
        minHeight: props.variation === 'small' ? '40px' : '56px',
        cursor: 'pointer',
        borderColor: props.hasError ? spotnanaTheme.colors.error : 'border.regular',
        transition: 'all 0.2s ease-in-out',
        padding: props.hasLabel ? '18px 0 8px 16px' : '2px 0 2px 16px',
        paddingLeft: props.hasPrefix ? '48px' : undefined,
        backgroundColor: props.naked
            ? spotnanaTheme.colors.bg.transparent
            : /**
             * In some cases(Agent desktop -> TaskStatus) where a custom background color was being provided to the Control component of select, bg.regular
             * was overriding it. Hence, added a prop that would set the parent color to undefined for it
             */
                props.overrideParentColor
                    ? undefined
                    : 'bg.regular',
        ':hover:not(:focus)': {
            borderColor: 'border.regularHover',
        },
    },
    '.select__value-container': Object.assign({ padding: 0 }, props.containerStyles),
    '.select__control--is-focused': {
        borderColor: props.theme.colors.inputHighlightBorder,
    },
    '.select__indicator': {
        marginTop: props.hasLabel ? -12 : 0,
        padding: '0 8px 0 8px',
        display: props.naked ? 'none' : 'default',
        ' svg': {
            width: 24,
            height: 24,
        },
    },
    '.select__control--menu-is-open .select__dropdown-indicator': {
        transform: props.async ? 'rotate(0deg)' : 'rotate(180deg)',
    },
    '.select__menu': Object.assign({ border: 0, paddingX: 1, zIndex: 1401, boxShadow: '0px 2px 10px 0px rgba(16, 24, 40, 0.08), 0px 1px 4px 0px rgba(16, 24, 40, 0.15)', backgroundColor: 'bg.regular' }, props.menuContainerStyles),
    [themeSelector('dark')]: {
        '.select__menu': {
            backgroundColor: props.theme.colors.bg.surfaceMenu,
        },
    },
    '.select__menu-list': Object.assign({}, props.menuContainerStyles),
    '.select__control--is-disabled': {
        borderColor: props.theme.colors.inputBorder,
        backgroundColor: props.naked ? spotnanaTheme.colors.bg.transparent : props.theme.colors.inputHighlight,
    },
}), ({ hasLabel, readonly, naked }) => css({
    cursor: readonly && !naked ? 'not-allowed' : 'pointer',
    backgroundColor: readonly && !naked ? 'bg.blueGray' : 'bg.transparent',
    '.rfs-control-container >div:first-child >div:first-child': {
        top: hasLabel ? '40px' : '50%',
        fontSize: hasLabel ? 16 : 14,
        textTransform: 'capitalize',
        color: readonly && !naked ? 'text.label' : 'text.semiBold',
    },
}));
export const SelectLabel = styled.label(css({
    fontSize: 'label',
    position: 'absolute',
    color: 'text.label',
}), (props) => props.hide
    ? {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        width: 1,
    }
    : {
        left: 20,
        top: 10,
        cursor: props.readonly ? 'not-allowed' : 'pointer',
    });
const SelectPrefix = styled.div(css({
    position: 'absolute',
    top: '50%',
    left: 16,
    transform: 'translateY(-50%)',
}));
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const makeStylesOverride = (stylesFunc, styles, props) => stylesFunc ? stylesFunc(styles, props) : styles;
const Select = (_a) => {
    var { label, variation, creatable = false, async = false, hideDropdown = false, readonly = false, hasLabel = true, required = false, isSearchable = false, naked = false, createOptionPosition = undefined, overrideParentColor = false, containerStyles, menuContainerStyles, menuStyles, styles = {}, errorMessage, dataTestId, preFix, hasError, onCreateNewOption, loadOptions, onMenuOpen, onMenuClose, expandedWidth } = _a, restProps = __rest(_a, ["label", "variation", "creatable", "async", "hideDropdown", "readonly", "hasLabel", "required", "isSearchable", "naked", "createOptionPosition", "overrideParentColor", "containerStyles", "menuContainerStyles", "menuStyles", "styles", "errorMessage", "dataTestId", "preFix", "hasError", "onCreateNewOption", "loadOptions", "onMenuOpen", "onMenuClose", "expandedWidth"]);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    // This helps in overriding default react-select components if needed
    // Read more here: https://react-select.com/components
    const overriddenComponents = {
        DropdownIndicator: null,
    };
    const stylesOverride = Object.assign(Object.assign({}, styles), { indicatorSeparator: (provided, props) => makeStylesOverride(styles.indicatorSeparator, Object.assign(Object.assign({}, provided), { display: 'none' }), props), singleValue: (provided, props) => makeStylesOverride(styles.singleValue, Object.assign(Object.assign({}, provided), { overflow: 'hidden', color: props.isDisabled && !naked ? colors.text.label : colors.text.semiBold, opacity: props.isDisabled && !naked ? 0.4 : 1, fontSize: variation === 'large' ? '16px' : '14px', lineHeight: variation === 'large' ? '20px' : '16px' }), props), multiValue: (provided, props) => makeStylesOverride(styles.multiValue, Object.assign(Object.assign({}, provided), { backgroundColor: props.data.error ? colors.error : colors.bg.blueGray }), props), multiValueLabel: (provided, props) => makeStylesOverride(styles.multiValueLabel, Object.assign(Object.assign({}, provided), { color: props.data.error ? colors.text.white : colors.text.semiBold, fontSize: variation === 'large' ? '16px' : '14px' }), props), multiValueRemove: (provided, props) => makeStylesOverride(styles.multiValueRemove, Object.assign(Object.assign({}, provided), { color: props.data.error ? colors.text.white : colors.text.semiBold, 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            ':hover': {
                backgroundColor: props.data.error ? colors.errorBackground : colors.bg.lightBlueGrey,
                color: props.data.error ? colors.error : colors.text.semiBold,
            } }), props), placeholder: (provided, props) => makeStylesOverride(styles.placeholder, Object.assign(Object.assign({}, provided), { color: colors.text.label, fontSize: variation === 'large' ? '16px' : '14px' }), props), menu: (provided, props) => makeStylesOverride(styles.menu, Object.assign(Object.assign({}, provided), menuStyles), props), option: (provided, props) => makeStylesOverride(styles.option, Object.assign(Object.assign(Object.assign({}, provided), { borderRadius: '8px', padding: '10px', cursor: 'pointer', color: spotnanaTheme.colors.onSurface3, backgroundColor: props.isFocused ? spotnanaTheme.colors.inputHighlight : '', 
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            '&:hover': {
                backgroundColor: spotnanaTheme.colors.inputHighlight,
            } }), (props.isDisabled
            ? {
                opacity: naked ? 1 : 0.5,
                cursor: naked ? 'default' : 'not-allowed',
            }
            : {})), props) });
    const selectTheme = (theme) => (Object.assign(Object.assign({}, theme), { borderRadius: 8, colors: Object.assign(Object.assign({}, theme.colors), { danger: '#dc3545', primary: '#F7F9FB', dangerLight: 'rgba(220, 53, 69, 0.25)' }) }));
    const handleKeyDown = (event) => {
        if (!restProps.inputValue)
            return;
        switch (event.key) {
            case 'Enter':
            case 'Tab':
                event.preventDefault();
                if (onCreateNewOption)
                    onCreateNewOption();
                break;
            default:
            // do nothing
        }
        if (restProps.onKeyDown)
            restProps.onKeyDown(event);
    };
    const handleBlurForCreatable = (event) => {
        if (restProps.inputValue && onCreateNewOption) {
            onCreateNewOption();
        }
        if (restProps.onBlur)
            restProps.onBlur(event);
    };
    const handleOnMenuOpen = () => {
        setIsMenuOpen(true);
        onMenuOpen === null || onMenuOpen === void 0 ? void 0 : onMenuOpen();
    };
    const handleOnMenuClose = () => {
        setIsMenuOpen(false);
        onMenuClose === null || onMenuClose === void 0 ? void 0 : onMenuClose();
    };
    const canExpandMenu = expandedWidth && isMenuOpen;
    let expandMenuBoxProps = {
        width: '100%',
    };
    if (canExpandMenu) {
        // expands the Select when menu is open if expandedWidth has a width value
        expandMenuBoxProps = {
            width: expandedWidth,
            position: 'relative',
            zIndex: 4,
        };
    }
    return (React.createElement(Box, Object.assign({}, expandMenuBoxProps),
        React.createElement(SelectWrapper, { variation: variation, hasError: hasError || (errorMessage ? errorMessage !== '' : false), containerStyles: containerStyles, menuContainerStyles: menuContainerStyles, hasLabel: hasLabel && variation === 'large', readonly: readonly, "data-testid": dataTestId, "data-readonly": readonly, hasPrefix: Boolean(preFix), async: async, naked: naked, overrideParentColor: overrideParentColor },
            creatable ? (
            /**
             * We do not want to override the event handlers added to this component.
             * So all custom event handlers should be defined after the spread of restProps
             */
            React.createElement(CreatableSelectLib, Object.assign({ menuPlacement: "auto", components: hideDropdown ? overriddenComponents : undefined, menuIsOpen: hideDropdown ? false : undefined, instanceId: restProps.inputId, isDisabled: readonly, "data-testid": dataTestId, classNamePrefix: "select", styles: stylesOverride, theme: selectTheme }, restProps, { createOptionPosition: createOptionPosition, onKeyDown: handleKeyDown, onBlur: handleBlurForCreatable, onMenuOpen: handleOnMenuOpen, onMenuClose: handleOnMenuClose }))) : async ? (React.createElement(AsyncSelectLib, Object.assign({ menuPlacement: "auto", classNamePrefix: "select", styles: stylesOverride, theme: selectTheme, "data-testid": dataTestId, instanceId: restProps.inputId, isDisabled: readonly, loadOptions: loadOptions, onMenuOpen: handleOnMenuOpen, onMenuClose: handleOnMenuClose }, restProps))) : (React.createElement(SelectLib, Object.assign({ menuPlacement: "auto", components: hideDropdown ? overriddenComponents : undefined, instanceId: restProps.inputId, isDisabled: readonly, classNamePrefix: "select", isSearchable: isSearchable, "data-testid": dataTestId, styles: stylesOverride, theme: selectTheme, onMenuOpen: handleOnMenuOpen, onMenuClose: handleOnMenuClose }, restProps))),
            React.createElement(SelectLabel, { hide: !hasLabel || variation === 'small', htmlFor: restProps.inputId, readonly: readonly, className: "v1-input-label" }, required ? `${label} *` : label),
            React.createElement(SelectPrefix, null, preFix))));
};
export const OptionContainer = (props) => {
    const { children } = props;
    return React.createElement(components.Option, Object.assign({}, props), children);
};
/**
 * @deprecated
 */
export default Select;
